/* Admin Recycle Bin — list soft-deleted applications, restore them, or
 * permanently purge.  Also includes a "Find application by token" lookup
 * tool to investigate orphaned borrower / shared links that point to apps
 * that may have been deleted or purged.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Trash2, RotateCcw, Search, AlertTriangle, RefreshCw, ExternalLink, Stethoscope, Wrench } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch (_e) { return s; }
};

const RecycleBin = ({ token }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [busyRow, setBusyRow] = useState(null);

  const [lookupToken, setLookupToken] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);

  const [nameQuery, setNameQuery] = useState('');
  const [nameSearching, setNameSearching] = useState(false);
  const [nameResults, setNameResults] = useState(null);
  const [nameBusyId, setNameBusyId] = useState(null);
  const [diagnoseId, setDiagnoseId] = useState(null);
  const [diagnosis, setDiagnosis] = useState(null);
  const [repairId, setRepairId] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/recycle-bin`, { headers });
      setItems(data?.applications || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load recycle bin');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleRestore = async (row) => {
    if (!window.confirm(`Restore "${row.business_name || row.applicant_name || row.id}" back to active applications?`)) return;
    setBusyRow(row.id);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/applications/${row.id}/restore`, {}, { headers });
      toast.success('Application restored');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    } finally {
      setBusyRow(null);
    }
  };

  const handlePurge = async (row) => {
    const label = row.business_name || row.applicant_name || row.id;
    if (!window.confirm(`Permanently delete "${label}"?\n\nThis removes the application, every message, every uploaded file (GridFS + local), and notes.  This action CANNOT be undone.`)) return;
    if (!window.confirm(`Final confirmation — "${label}" will be irreversibly purged. Continue?`)) return;
    setBusyRow(row.id);
    try {
      const { data } = await axios.delete(`${BACKEND_URL}/api/admin/applications/${row.id}/purge`, { headers });
      toast.success(`Purged. Removed ${data?.gridfs_objects_removed || 0} file(s) from storage.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Purge failed');
    } finally {
      setBusyRow(null);
    }
  };

  const handleLookup = async () => {
    const t = (lookupToken || '').trim();
    if (!t) {
      toast.error('Paste a token first');
      return;
    }
    setLookupLoading(true);
    setLookupResult(null);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/lookup-by-token`,
        { headers, params: { token: t } }
      );
      setLookupResult(data);
      if ((data?.match_count || 0) === 0) toast.info('No matching link found.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Lookup failed');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleNameSearch = async () => {
    const q = (nameQuery || '').trim();
    if (q.length < 2) {
      toast.error('Enter at least 2 characters');
      return;
    }
    setNameSearching(true);
    setNameResults(null);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/applications/search`,
        { headers, params: { q } }
      );
      setNameResults(data);
      if ((data?.count || 0) === 0) toast.info('No applications matched.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Search failed');
    } finally {
      setNameSearching(false);
    }
  };

  const handleRestoreFromSearch = async (row) => {
    if (!window.confirm(`Restore "${row.business_name || row.applicant_name || row.id}" back to active applications?`)) return;
    setNameBusyId(row.id);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/applications/${row.id}/restore`, {}, { headers });
      toast.success('Application restored');
      // Refresh both the recycle bin list and the search results.
      load();
      handleNameSearch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    } finally {
      setNameBusyId(null);
    }
  };

  const handleDiagnose = async (row) => {
    setDiagnoseId(row.id);
    setDiagnosis(null);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/applications/${row.id}/diagnose`,
        { headers },
      );
      setDiagnosis(data);
      if (data.parses_cleanly) {
        toast.success('Record is valid — no schema errors found.');
      } else {
        toast.info(`Record has ${(data.validation_errors || []).length} schema issue(s).`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Diagnose failed');
    } finally {
      setDiagnoseId(null);
    }
  };

  const handleRepair = async (row) => {
    if (!window.confirm(`Attempt to auto-repair "${row.business_name || row.applicant_name || row.id}"?\n\nThis coerces malformed list/dict fields (e.g. debt_schedule stored as null or wrong type) back to their proper defaults. It does NOT touch legitimate data — only empties fields that are already invalid.`)) return;
    setRepairId(row.id);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/applications/${row.id}/repair`,
        {},
        { headers },
      );
      const fixedFields = Object.keys(data?.changes || {});
      if (fixedFields.length === 0) {
        toast.info(data?.message || 'Nothing to repair');
      } else if (data?.parses_cleanly_after) {
        toast.success(`Repaired ${fixedFields.length} field(s). Record is now schema-valid.`);
      } else {
        toast.warning(`Repaired ${fixedFields.length} field(s), but ${(data?.remaining_errors || []).length} issue(s) remain.`);
      }
      // Re-run diagnose so the inline panel reflects the new state.
      await handleDiagnose(row);
      // Refresh search results too (status/timestamps may have changed).
      handleNameSearch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Repair failed');
    } finally {
      setRepairId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="recycle-bin-panel">
      {/* Search by business/borrower name — finds live + deleted apps */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="h-4 w-4" />
            Find Application by Business or Borrower Name
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-500">
            Search by business name, applicant name, email, or phone. Includes soft-deleted apps in the recycle bin, so if a record vanished from the pipeline while you were editing it, it will still surface here and can be restored in one click.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="e.g. Acme Trucking or jane@example.com"
              value={nameQuery}
              onChange={(e) => setNameQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleNameSearch(); }}
              className="bg-white text-slate-900 placeholder:text-slate-400 text-sm"
              data-testid="rb-name-search-input"
            />
            <Button
              onClick={handleNameSearch}
              disabled={nameSearching}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="rb-name-search-go"
            >
              {nameSearching ? 'Searching…' : 'Search'}
            </Button>
          </div>
          {nameResults && (
            <div className="text-xs bg-slate-50 border border-slate-200 rounded p-3 space-y-2" data-testid="rb-name-search-result">
              {nameResults.count === 0 ? (
                <p className="text-slate-600">No matching applications found for &quot;{nameResults.query}&quot;.</p>
              ) : (
                <>
                  <p className="text-slate-600">
                    {nameResults.count} match{nameResults.count === 1 ? '' : 'es'} for &quot;{nameResults.query}&quot;
                  </p>
                  <div className="space-y-2">
                    {nameResults.results.map((row) => (
                      <React.Fragment key={row.id}>
                      <div className="border border-slate-200 rounded p-2.5 bg-white flex items-start justify-between gap-3" data-testid={`rb-search-row-${row.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm text-slate-800 truncate">
                              {row.business_name || row.applicant_name || '(unnamed)'}
                            </p>
                            <span className="text-[10px] uppercase tracking-wide text-slate-400">{row.product_type || '—'}</span>
                            {row.in_recycle_bin ? (
                              <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px]">In Recycle Bin</Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px]">Live · {row.status || '—'}</Badge>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            {row.applicant_name ? `Applicant: ${row.applicant_name}` : ''}
                            {row.email ? ` · ${row.email}` : ''}
                            {row.phone ? ` · ${row.phone}` : ''}
                          </p>
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            Created {fmtDate(row.created_at)} · Updated {fmtDate(row.updated_at)}
                          </p>
                          {row.in_recycle_bin && (
                            <p className="text-[11px] text-amber-700 mt-0.5">
                              Deleted {fmtDate(row.deleted_at)} by {row.deleted_by_name || row.deleted_by || '?'}
                              {row.deleted_reason ? ` — “${row.deleted_reason}”` : ''}
                            </p>
                          )}
                          <p className="text-[10px] text-slate-400 mt-0.5 font-mono">{row.id}</p>
                        </div>
                        <div className="flex flex-col gap-1.5 shrink-0">
                          {row.in_recycle_bin ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRestoreFromSearch(row)}
                              disabled={nameBusyId === row.id}
                              data-testid={`rb-search-restore-${row.id}`}
                            >
                              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                try {
                                  navigator.clipboard?.writeText(row.id);
                                  toast.success('Application ID copied');
                                } catch (_e) {
                                  toast.error('Could not copy — the ID is shown below the row');
                                }
                              }}
                              data-testid={`rb-search-copy-${row.id}`}
                            >
                              <ExternalLink className="h-3.5 w-3.5 mr-1" /> Copy ID
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDiagnose(row)}
                            disabled={diagnoseId === row.id}
                            className="border-slate-300 text-slate-700"
                            data-testid={`rb-search-diagnose-${row.id}`}
                            title="Show why this app may not be rendering normally"
                          >
                            <Stethoscope className="h-3.5 w-3.5 mr-1" />
                            {diagnoseId === row.id ? 'Checking…' : 'Diagnose'}
                          </Button>
                        </div>
                      </div>
                      {diagnosis && diagnosis.id === row.id && (
                        <div className={`mt-2 rounded border p-2.5 text-[11px] ${diagnosis.parses_cleanly ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`} data-testid={`rb-search-diagnosis-${row.id}`}>
                          {diagnosis.parses_cleanly ? (
                            <p>
                              ✓ Record is <strong>schema-valid</strong>. If it isn&apos;t showing in a pipeline, the cause is a role/product-type filter, not a corrupt record.
                              For underwriters: check the user&apos;s <em>Allowed Loan Products</em> in Edit User.
                            </p>
                          ) : (
                            <>
                              <p className="font-semibold mb-1">Record failed schema validation ({(diagnosis.validation_errors || []).length} issue{(diagnosis.validation_errors || []).length === 1 ? '' : 's'}). The list view now shows it as a minimal entry — open it to fix the offending field.</p>
                              <ul className="list-disc ml-4 space-y-0.5">
                                {(diagnosis.validation_errors || []).slice(0, 8).map((err, i) => (
                                  <li key={i} className="font-mono">
                                    {Array.isArray(err.loc) ? err.loc.join('.') : ''}
                                    {err.type ? ` [${err.type}]` : ''}
                                    {err.msg ? ` — ${err.msg}` : ''}
                                    {err.input !== undefined && err.input !== null ? ` (input: ${JSON.stringify(err.input).slice(0, 60)})` : ''}
                                  </li>
                                ))}
                                {(diagnosis.validation_errors || []).length > 8 && (
                                  <li>… and {(diagnosis.validation_errors || []).length - 8} more</li>
                                )}
                              </ul>
                              <div className="mt-2 pt-2 border-t border-red-200 flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRepair(row)}
                                  disabled={repairId === row.id}
                                  className="border-red-300 bg-white text-red-800 hover:bg-red-100"
                                  data-testid={`rb-search-repair-${row.id}`}
                                >
                                  <Wrench className="h-3.5 w-3.5 mr-1" />
                                  {repairId === row.id ? 'Repairing…' : 'Auto-Repair'}
                                </Button>
                                <span className="text-[10px] text-red-700">Coerces malformed list/dict fields back to defaults. Legitimate data is not touched.</span>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      </React.Fragment>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lookup tool */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="h-4 w-4" />
            Find Application by Share / Borrower Link Token
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-500">
            Paste the token portion of any shared link (e.g. <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">/borrower/&lt;TOKEN&gt;</code> or <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">/debt-schedule/&lt;TOKEN&gt;</code>).
            The tool will tell you whether the underlying application is live, in the recycle bin, or has been permanently purged.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="449b107a-3ab3-4cb5-8ef7-996667b46fe6"
              value={lookupToken}
              onChange={(e) => setLookupToken(e.target.value)}
              className="bg-white text-slate-900 placeholder:text-slate-400 font-mono text-xs"
              data-testid="rb-lookup-token-input"
            />
            <Button
              onClick={handleLookup}
              disabled={lookupLoading}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="rb-lookup-go"
            >
              {lookupLoading ? 'Searching…' : 'Lookup'}
            </Button>
          </div>
          {lookupResult && (
            <div className="text-xs bg-slate-50 border border-slate-200 rounded p-3 space-y-2" data-testid="rb-lookup-result">
              {lookupResult.match_count === 0 ? (
                <p className="text-slate-600">
                  No link record found for this token in any collection
                  (<code>borrower_completion_links</code>, <code>doc_upload_links</code>, <code>underwriting_share_links</code>, etc.).
                  The link record itself was likely purged.
                </p>
              ) : (
                lookupResult.matches.map((m, i) => (
                  <div key={i} className="border-l-2 border-slate-300 pl-2">
                    <p className="text-slate-700">
                      <strong>{m.link_collection}</strong> — <span className={
                        m.application_status === 'live' ? 'text-emerald-700' :
                        m.application_status === 'in_recycle_bin' ? 'text-amber-700' :
                        'text-red-700'
                      }>{m.application_status || 'no application linked'}</span>
                    </p>
                    {m.application && (
                      <div className="mt-1 text-slate-600 space-y-0.5">
                        <div>App ID: <code className="text-[11px]">{m.application.id}</code></div>
                        <div>Business: {m.application.business_name || '—'} · Applicant: {m.application.applicant_name || '—'}</div>
                        <div>Product: {m.application.product_type} · Status: {m.application.status}</div>
                        <div>Created: {fmtDate(m.application.created_at)} · Last updated: {fmtDate(m.application.updated_at)}</div>
                        {m.application.deleted_at && (
                          <div className="text-amber-700">
                            Deleted {fmtDate(m.application.deleted_at)} by {m.application.deleted_by_name || m.application.deleted_by || '?'}
                            {m.application.deleted_reason ? ` — “${m.application.deleted_reason}”` : ''}
                          </div>
                        )}
                      </div>
                    )}
                    {m.application_status === 'purged_or_missing' && (
                      <div className="mt-1 text-red-700">
                        The application referenced by this link (id <code className="text-[11px]">{m.application_id}</code>) has been permanently purged.  Records were destroyed before the soft-delete bin was rolled out.
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recycle bin list */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-red-600" />
            Recycle Bin
            {items.length > 0 && <Badge variant="outline" className="ml-1">{items.length}</Badge>}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="rb-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="rb-empty">Bin is empty.  Deleted applications will appear here for review and recovery.</p>
          ) : (
            <div className="space-y-2" data-testid="rb-list">
              {items.map((row) => (
                <div key={row.id} className="border border-slate-200 rounded-lg p-3 flex items-start justify-between gap-3 hover:bg-slate-50 transition-colors" data-testid={`rb-row-${row.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm">
                      {row.business_name || row.applicant_name || '(unnamed)'}
                      <span className="text-slate-400 font-normal ml-2 text-xs">{row.product_type}</span>
                    </p>
                    <p className="text-xs text-slate-600 mt-0.5">
                      {row.applicant_name ? `Applicant: ${row.applicant_name} · ` : ''}
                      Was: <span className="text-slate-700">{row.status_before_delete || '—'}</span>
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Deleted {fmtDate(row.deleted_at)} by {row.deleted_by_name || row.deleted_by || '?'}
                      {row.deleted_reason ? ` — “${row.deleted_reason}”` : ''}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRestore(row)}
                      disabled={busyRow === row.id}
                      data-testid={`rb-restore-${row.id}`}
                    >
                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePurge(row)}
                      disabled={busyRow === row.id}
                      className="border-red-300 text-red-700 hover:bg-red-50"
                      data-testid={`rb-purge-${row.id}`}
                    >
                      <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Purge
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RecycleBin;
